export interface ApiErrorBody {
  request_id?: string;
  error: {
    code: string;
    message: string;
    hint?: string;
  };
}

export class AspError extends Error {
  readonly code: string;
  readonly hint?: string;
  readonly requestId?: string;
  readonly statusCode?: number;
  readonly exitCode: number;

  constructor(
    message: string,
    options: {
      code?: string;
      hint?: string;
      requestId?: string;
      statusCode?: number;
      exitCode?: number;
      cause?: unknown;
    } = {},
  ) {
    super(message, { cause: options.cause });
    this.name = "AspError";
    this.code = options.code ?? "UNKNOWN";
    this.hint = options.hint;
    this.requestId = options.requestId;
    this.statusCode = options.statusCode;
    this.exitCode = options.exitCode ?? 1;
  }

  static fromResponse(status: number, body: unknown): AspError {
    const parsed = body as Partial<ApiErrorBody> & {
      error?: ApiErrorBody["error"] & { detail?: string };
    };
    if (parsed?.error) {
      const exitCode = mapErrorCodeToExit(parsed.error.code, status);
      let hint = parsed.error.hint ?? parsed.error.detail;
      if (parsed.error.code === "ASP.COMM.NOT_FOUND") {
        hint = hint ?? "The API endpoint is not available on this Platform environment.";
      }
      return new AspError(parsed.error.message, {
        code: parsed.error.code,
        hint,
        requestId: parsed.request_id,
        statusCode: status,
        exitCode,
      });
    }
    return new AspError(`HTTP ${status}`, {
      code: "HTTP_ERROR",
      statusCode: status,
      exitCode: status >= 500 ? 2 : 1,
    });
  }
}

function mapErrorCodeToExit(code: string, status: number): number {
  if (
    code === "UNAUTHORIZED" ||
    code === "UNAUTHENTICATED" ||
    code === "ASP.AUTH.UNAUTHORIZED" ||
    code === "ASP.AUTH.SESSION_INVALID" ||
    code === "SESSION_INVALID"
  ) {
    return 3;
  }
  if (code === "FORBIDDEN") return 4;
  if (status >= 500) return 2;
  return 1;
}

export function maskSecrets(text: string): string {
  return text
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer [REDACTED]")
    .replace(/"access_token"\s*:\s*"[^"]+"/gi, '"access_token":"[REDACTED]"')
    .replace(/"refresh_token"\s*:\s*"[^"]+"/gi, '"refresh_token":"[REDACTED]"')
    .replace(/"token"\s*:\s*"[^"]+"/gi, '"token":"[REDACTED]"');
}
